import { useCallback } from 'react';
import { SMSTag } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { useDismissTagMutation } from '../mutations';
import { useSMSDataV3QueryUpdaters } from '../query-updaters';

const DEFAULT_DELAY = 3500;

type UseDismissTagWithUndoArgs = {
  onSuccess: (alertActionOnClick: () => void, autoDismissAfter: number) => void;
  delay?: number;
  mutationOptions?: Parameters<typeof useDismissTagMutation>[0];
};

export const useDismissTagWithUndo = ({
  onSuccess,
  delay = DEFAULT_DELAY,
  mutationOptions,
}: UseDismissTagWithUndoArgs) => {
  const dismissTagMutation = useDismissTagMutation(mutationOptions);
  const { updateSMS } = useSMSDataV3QueryUpdaters();
  return useCallback(
    (
      {
        originalAppliedBy,
        smsCreatedAt,
        ...request
      }: Parameters<typeof dismissTagMutation.mutate>[0] & { originalAppliedBy: string; smsCreatedAt: string },
      options?: Parameters<typeof dismissTagMutation.mutate>[1]
    ) => {
      const timeout = setTimeout(() => {
        dismissTagMutation.mutate(request, options);
      }, delay + 1000);
      updateSMS({
        matchValues: {
          id: request.smsId,
          threadId: request.threadId,
          locationId: request.groupId,
        },
        newValues: (sms) => {
          return {
            ...sms,
            tags: sms.tags.filter((id) => id !== request.tagId),
            tagsDetailed: sms.tagsDetailed?.filter((tag) => tag.tagId !== request.tagId),
          };
        },
        skipInvalidation: true,
      });
      onSuccess(() => {
        clearTimeout(timeout);

        updateSMS({
          matchValues: {
            id: request.smsId,
            threadId: request.threadId,
            locationId: request.groupId,
          },
          newValues: (sms) => {
            const newTagsDetailed: (typeof sms)['tagsDetailed'] = sms.tagsDetailed?.some(
              (tag) => tag.tagId === request.tagId
            )
              ? sms.tagsDetailed
              : [
                  ...(sms.tagsDetailed ?? []),
                  {
                    tagId: request.tagId,
                    smsId: request.smsId,
                    appliedBy: originalAppliedBy,
                    smsCreatedAt,
                  } satisfies SMSTag,
                ];
            return {
              ...sms,
              tags: Array.from(new Set([...sms.tags, request.tagId])),
              tagsDetailed: newTagsDetailed,
            };
          },
        });
      }, delay);
    },
    [delay, dismissTagMutation, updateSMS, onSuccess]
  );
};
